import { GetterTree } from 'vuex'
import {
  UserFull,
  UserStatus,
  CompanyUser,
  UserInvitation,
  CompanyRole,
  UserScope,
  scriptScopeSubtypes,
  knowledgeScopeSubtypes,
  UserFilter,
} from '@/models/users'
import i18n from '@/i18n'
import { RootState } from '../../state'
import { UserState } from './state'
import _sortBy from 'lodash.sortby'

export const getters: GetterTree<UserState, RootState> = {
  /**
   * Get my user from users state
   */
  getMe: (state: UserState): UserFull => {
    return state.me
  },

  /**
   * Get method to check scope authorization for current user
   *
   * @returns {(checkingScope:UserScope) => boolean} authorization function
   */
  canDoScope: (
    state: UserState,
    getters,
  ): ((checkingScope: UserScope) => boolean) => (
    checkingScope: UserScope,
  ): boolean => {
    if (
      state.me.roles !== undefined &&
      getters.getActiveCompany?.id &&
      state.me.roles[getters.getActiveCompany?.id]?.scope
    ) {
      const scopes = state.me.roles[getters.getActiveCompany.id].scope

      const allScriptScope =
        scriptScopeSubtypes.includes(checkingScope) &&
        scopes.includes(UserScope.Scripts)

      const allKnowledgeScope =
        knowledgeScopeSubtypes.includes(checkingScope) &&
        scopes.includes(UserScope.Knowledge)

      const systemCompanyScope =
        checkingScope === UserScope.SystemCompany &&
        !!getters.getActiveCompany?.system

      return (
        allScriptScope ||
        allKnowledgeScope ||
        systemCompanyScope ||
        scopes.includes(checkingScope)
      )
    }
    return false
  },

  /**
   * Compute filtered list of users
   *
   * @returns {CompanyUser[]}
   */
  getUsersFilteredList: (state: UserState): CompanyUser[] => {
    if (state.filter === UserStatus.Invitation) {
      return []
    }
    if (state.filter !== UserStatus.All) {
      return state.list.filter(
        (user: CompanyUser) => user.active === !!state.filter,
      )
    }
    return state.list
  },

  /**
   * Compute filtered list of invitations
   *
   * @returns {UserInvitation[]}
   */
  getInvitationsList: (state: UserState): UserInvitation[] => {
    if (
      state.filter === UserStatus.All ||
      state.filter === UserStatus.Invitation
    ) {
      return state.invitations
    }
    return []
  },

  getTotalActiveUsers: (state: UserState): number => {
    return state.list.length + state.invitations.length
  },

  getFilters: (state: UserState): UserFilter[] => {
    const filters = {
      [UserStatus.Invitation]: {
        label: i18n.tc('user.categories.invited'),
        status: UserStatus.Invitation,
        count: state.invitations.length,
        scope: [UserScope.Users, UserScope.Roles],
      },
      [UserStatus.Inactive]: {
        label: i18n.tc('user.categories.removed'),
        status: UserStatus.Inactive,
        count: 0,
        scope: [UserScope.Users, UserScope.Roles],
      },
      [UserStatus.All]: {
        label: i18n.tc('user.categories.all'),
        status: UserStatus.All,
        count: state.list.length + state.invitations.length,
        scope: [UserScope.Users, UserScope.Roles],
      },
      [UserStatus.Active]: {
        label: i18n.tc('user.categories.active'),
        status: UserStatus.Active,
        count: 0,
        scope: [],
      },
    }
    state.list.forEach((one: CompanyUser) => {
      if (one.active) {
        filters[UserStatus.Active].count++
      } else {
        filters[UserStatus.Inactive].count++
      }
    })
    return _sortBy(
      Object.values(filters),
      (one: UserFilter) => one.status,
    ).reverse()
  },

  /**
   * Get role by it's id
   *
   * @returns {(roleId: number) => CompanyRole | undefined}
   */
  getRoleById: (
    state: UserState,
  ): ((roleId: number) => CompanyRole | undefined) => (
    roleId: number,
  ): CompanyRole | undefined => {
    return state.roles.find(r => `${r.id}` === `${roleId}`)
  },
}
